import { USER_PERMISSIONS } from "Constants";
import { NavBarLink } from "Models/nav.bar.link";
import { Resources, RolePermissionsResponse } from "Models/resources";
import { ResourceCanEnum, ResourceName, ResourceObjectTypeEnum } from "Util/enums/accessRule";
import { links } from "Util/links";
import { checkResourceCan } from "Util/permissions";
import { isMoving } from "Util/validation";
import { compareNavBarLink } from "./compareNavBarLinkUtils";
import { useAppState } from "Context/StateContext";
import { useEffect, useState } from "react";
import { NavBarLinks } from "Types";
import { useHistory } from "react-router";

export const useResourcePermissionsActions = () => {
  const { currentUser, setPathPermissions, userPermissions } = useAppState();

  const [navBarLinks, setNavBarLinks] = useState<NavBarLinks>();
  const [showButtonNetwork, setShowButtonNetwork] = useState<boolean>(false);
  const [navBarLinkSelected, setNavBarLinkSelected] = useState<NavBarLink>();
  const [groupSelected, setGroupSelected] = useState<string>();
  const history = useHistory();

  const getInitialUserResources = (): RolePermissionsResponse[] => {
    const permissions = localStorage.getItem(USER_PERMISSIONS);

    if (permissions) {
      return JSON.parse(permissions)?.filter(
        (permission: RolePermissionsResponse) =>
          permission?.objectType === ResourceObjectTypeEnum.RESOURCE
      );
    }

    if (userPermissions) {
      return userPermissions?.filter(
        (permission: RolePermissionsResponse) =>
          permission?.objectType === ResourceObjectTypeEnum.RESOURCE
      );
    }

    return [];
  };

  const loadResources = (updateNavBarLinks?: boolean, pathname?: string) => {
    const navBarLinksFiltered: { [x: string]: Array<NavBarLink> } = {};

    const PATHNAME = pathname || location.pathname;

    if (currentUser?.company?.profile) {
      const linksByCompany = links[currentUser?.company?.profile?.initials];
      const groups = linksByCompany ? Object.keys(linksByCompany) : [];
      const isMovingCompany = isMoving(currentUser?.company);

      let pathPermissions: { route: string; isShow: boolean }[] = [];
      let userResources: RolePermissionsResponse[] = getInitialUserResources();

      if (!groups?.length) return;
      groups.forEach((group) => {
        const navBarLinks = linksByCompany[group] ?? [];

        const navBarLinksInResource: Array<NavBarLink> = [];

        navBarLinks.forEach((navBarLink) => {
          if (!currentUser) return;

          if (isMovingCompany && userResources?.length) {
            userResources.forEach((resource) => {
              if (!resource?.objectDetails?.name) return;

              if (
                resource?.objectDetails?.name === "Network" &&
                checkResourceCan(userResources, ResourceName.NETWORK, ResourceCanEnum.VIEW)
              )
                setShowButtonNetwork(true);

              if (resource?.objectDetails?.name?.toLowerCase() === "suppliers") return;

              if (
                compareNavBarLink(navBarLink, resource?.objectDetails as Resources) &&
                resource?.canView === 1
              ) {
                if (
                  !pathPermissions?.find((path) => path?.route === navBarLink?.path) &&
                  navBarLink?.key?.toLowerCase() === resource?.objectDetails?.name?.toLowerCase()
                ) {
                  pathPermissions?.push({
                    route: navBarLink?.path,
                    isShow: resource?.canView === 1,
                  });
                }

                if (
                  resource?.objectDetails?.name === "Route Planner" &&
                  !currentUser?.company?.useRoutePlanner
                ) {
                  navBarLink.show = false;
                }

                if (navBarLink.show !== false) {
                  const isNavBarLinkExist = navBarLinksInResource?.some(
                    (nav) => nav?.key === navBarLink?.key
                  );
                  if (!isNavBarLinkExist) {
                    navBarLinksInResource.push(navBarLink);
                  }
                }
              }
            });
          }

          if (!isMovingCompany && currentUser?.resources?.length) {
            currentUser.resources.forEach((resource) => {
              if (!resource.name) return;
              if (resource.name === "Network") setShowButtonNetwork(true);
              if (resource.name.toLowerCase() === "suppliers") return;

              if (compareNavBarLink(navBarLink, resource) && resource?.roles_resources?.view) {
                if (resource.name === "Route Planner" && !currentUser?.company?.useRoutePlanner) {
                  navBarLink.show = false;
                }

                if (navBarLink.show !== false) {
                  const isNavBarLinkExist = navBarLinksInResource?.some(
                    (nav) => nav?.key === navBarLink?.key
                  );

                  if (!isNavBarLinkExist) {
                    navBarLinksInResource.push(navBarLink);
                  }
                }
              }
            });
          }

          if (PATHNAME?.includes(navBarLink.path)) {
            setNavBarLinkSelected(navBarLink);
            setGroupSelected(group);
          }
        });
        if (navBarLinksInResource?.length) {
          navBarLinksFiltered[group] = navBarLinksInResource;
        }
      });

      setPathPermissions(pathPermissions);
    }

    if (updateNavBarLinks) setNavBarLinks(navBarLinksFiltered);
  };

  useEffect(() => {
    loadResources(true);

    const unListener = history.listen((listener) => {
      loadResources(false, listener.pathname);
    });

    return () => unListener();
  }, [userPermissions]);

  return {
    navBarLinks,
    showButtonNetwork,
    navBarLinkSelected,
    groupSelected,
    setNavBarLinkSelected,
    setGroupSelected,
    loadResources,
  };
};
